import * as React from "react";
import { Form, Formik, FormikProps } from "formik";
import { default as dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { Stack, Box, Button } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { FormFieldObserver } from "components/formikFields/FormFieldObserver/FormFieldObserver";
import {
  DatePickerField,
  DATE_PICKER_FORMAT
} from "../../../../components/formikFields/DatePickerField/DatePickerField";

interface Props {
  from: Date;
  to: Date;
  onChange: (from: Date, to: Date) => void;
  onRefresh: () => void;
}

enum FieldNames {
  from = "from",
  to = "to"
}

interface Values {
  [FieldNames.from]: string;
  [FieldNames.to]: string;
}

dayjs.extend(utc);
dayjs.extend(timezone);

const Filter: React.FC<Props> = ({ from, to, onChange, onRefresh }) => {
  const formikRef = React.useRef<FormikProps<Values>>(null);
  const lastSubmittedValuesRef = React.useRef<Values | null>(null);

  const initialValue = React.useMemo(
    () => ({
      from: dayjs(from).format(DATE_PICKER_FORMAT),
      to: dayjs(to).format(DATE_PICKER_FORMAT)
    }),
    [from, to]
  );

  const validate = React.useCallback((values: Values) => {
    const from = values[FieldNames.from];
    const to = values[FieldNames.to];
    const fromValue = dayjs(from, DATE_PICKER_FORMAT);
    const toValue = dayjs(to, DATE_PICKER_FORMAT);
    const errors: Record<string, string> = {};

    if (!from) {
      errors[FieldNames.from] = "Required";
    }
    if (!to) {
      errors[FieldNames.to] = "Required";
    }
    if (from && to) {
      if (toValue.isBefore(fromValue)) {
        errors[FieldNames.to] = "Start date must be before end date";
      } else if (toValue.subtract(90, "day").isAfter(fromValue)) {
        errors[FieldNames.from] = "Search range cannot exceed 90 days";
      }
    }

    return errors;
  }, []);

  const onSubmit = React.useCallback(
    (values: Values) => {
      const fromValue = dayjs
        .tz(values[FieldNames.from], DATE_PICKER_FORMAT, "America/Los_Angeles")
        .startOf("day")
        .toDate();
      const toValue = dayjs.tz(values[FieldNames.to], DATE_PICKER_FORMAT, "America/Los_Angeles").endOf("day").toDate();
      lastSubmittedValuesRef.current = values;
      onChange(fromValue, toValue);
    },
    [onChange]
  );

  const onFormFieldObserverChange = React.useCallback(() => {
    const formValues = formikRef?.current?.values;
    if (formValues) {
      // Check if values have actually changed from last submission
      const lastValues = lastSubmittedValuesRef.current;
      if (
        lastValues &&
        lastValues[FieldNames.from] === formValues[FieldNames.from] &&
        lastValues[FieldNames.to] === formValues[FieldNames.to]
      ) {
        return; // Values haven't changed, don't submit
      }

      // Validate before submitting
      const errors = validate(formValues);
      if (Object.keys(errors).length === 0) {
        onSubmit(formValues);
      }
    }
  }, [onSubmit, validate]);

  return (
    <Formik
      initialValues={initialValue}
      validate={validate}
      onSubmit={onSubmit}
      enableReinitialize
      innerRef={formikRef}
    >
      <Form autoComplete="off" autoCorrect="off">
        <FormFieldObserver fieldName={FieldNames.from} onChange={onFormFieldObserverChange} />
        <FormFieldObserver fieldName={FieldNames.to} onChange={onFormFieldObserverChange} />
        <Box sx={{ display: "flex", alignItems: "flex-start" }}>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <DatePickerField name={FieldNames.from} label="Start date/time" required={true} disableFuture={true} />
            <DatePickerField name={FieldNames.to} label="End date/time" required={true} disableFuture={true} />
          </Stack>
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="contained" startIcon={<RefreshIcon />} onClick={onRefresh} sx={{ ml: 2 }}>
            Refresh
          </Button>
        </Box>
      </Form>
    </Formik>
  );
};

export { Filter };
